import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse
} from '@angular/common/http';
import { ErrorHandler } from '@angular/core';

export const QUEUED_PLANTATION_DELETE = new HttpContextToken(() => false);

// The section that sent the request shows its own inline error, so the
// interceptor skips the toast. Session handling and loading resets still run.
export const LOCALLY_HANDLED_HTTP_ERROR = new HttpContextToken(() => false);

export const LOCALLY_HANDLED = {
  context: new HttpContext().set(LOCALLY_HANDLED_HTTP_ERROR, true)
};

/**
 * For a catch that turns a failure into an inline error. The interceptor
 * already reports failed requests; anything else is a bug thrown while
 * handling the response, and still goes to Sentry.
 */
export function reportUnexpectedError(
  errorHandler: ErrorHandler,
  error: unknown
): void {
  if (!(error instanceof HttpErrorResponse)) errorHandler.handleError(error);
}

export function getRetryAfterDelay(error: unknown): number | undefined {
  if (!(error instanceof HttpErrorResponse) || error.status !== 429) return;
  const header = error.headers.get('Retry-After')?.trim();
  if (!header) return;
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : undefined;
}

function unwrapAngularError(error: unknown): unknown {
  if (error && typeof error === 'object') {
    if ('ngOriginalError' in error) return error.ngOriginalError;
    if ('rejection' in error) return error.rejection;
  }
  return error;
}

export function isExpectedHttpError(error: unknown): boolean {
  const original = unwrapAngularError(error);
  return (
    original instanceof HttpErrorResponse &&
    [401, 403, 429].includes(original.status)
  );
}

// Sentry's default Angular extractor replaces HTTP errors with their body,
// losing the status needed by beforeSend, including for Zone promise rejections.
export function preserveHttpError(
  error: unknown,
  defaultExtractor: (error: unknown) => unknown
): unknown {
  const original = unwrapAngularError(error);
  return original instanceof HttpErrorResponse
    ? original
    : defaultExtractor(error);
}
